import { Component, computed, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { EppoSpeciesService } from './service/eppo-species.service';

/**
 * Read-only display of a site's species (`eppoSpecies`).
 *
 * Until this existed the species were captured in `eppo-species-selector` and never shown again,
 * so anyone who had not entered them — a buyer looking at a supplier's chain — could not see them
 * at all (app#254).
 *
 * The card has no room for names, so the badge is icon + count and the names live in the tooltip.
 * They are fetched on first hover/focus, not on render: a large chain would otherwise fire a
 * lookup per code per site. The codes themselves are always in hand, so the tooltip is never
 * empty and never spins — the names simply replace them when they arrive, and stay replaced by
 * the code when the external EPPO service cannot resolve one.
 *
 * Visibility is NOT decided here. The server already nulls `eppoSpecies` for non-producer sites
 * from chain degree 2 (`ChainDegreeFilter.filterSite`, `ReportSite.geoVisible`); an empty input
 * renders nothing, which is the whole rule this component needs to know.
 */
@Component({
  selector: 'app-species-badge',
  imports: [MatIconModule, MatTooltipModule, TranslateModule],
  template: `
    @if (codes()?.length) {
      <span
        class="species-badge"
        data-test="species-badge"
        tabindex="0"
        [matTooltip]="('species_tr' | translate) + ': ' + speciesText()"
        [attr.aria-label]="('species_tr' | translate) + ': ' + speciesText()"
        (mouseenter)="resolveNames()"
        (focus)="resolveNames()"
      >
        <mat-icon>forest</mat-icon>
        {{ codes()!.length }}
      </span>
    }
  `,
  styles: `
    .species-badge {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      cursor: default;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }
  `,
})
export class SpeciesBadgeComponent {
  private readonly eppo = inject(EppoSpeciesService);
  private readonly resolvedNames = signal<string[] | null>(null);

  readonly codes = input<string[] | null | undefined>();

  protected readonly speciesText = computed(() => (this.resolvedNames() ?? this.codes() ?? []).join(', '));

  protected async resolveNames(): Promise<void> {
    if (this.resolvedNames()) return;
    const codes = this.codes();
    if (!codes?.length) return;

    // Claim the slot before awaiting, so a second hover during the request does not start another.
    this.resolvedNames.set(codes);
    this.resolvedNames.set((await this.eppo.resolveAll(codes)).map((s) => this.eppo.label(s)));
  }
}
